import { type Accessor, createEffect, createSignal, onCleanup } from "solid-js";

// Clip thumbnails are handed out before the server's queued job has extracted
// them, so their first loads 404 for as long as that job waits in the queue.
// About four minutes in all covers a backlog without polling forever.
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 30000, 60000, 120000];

// Streaming remounts thumbnails on every token. A probe's load event comes
// after the next paint in Firefox, even from the cache, so without this a
// shown image flashes back to its placeholder each time.
const loadedUrls = new Set<string>();

type FetchProbeResponse = Pick<Response, "ok" | "status" | "headers" | "body">;

const isImageResponse = (response: FetchProbeResponse): boolean => {
	const type = response.headers.get("content-type") ?? "";
	return type === "" || type.startsWith("image/");
};

/** CORS fetch avoids ORB when a cross-origin URL 404s with XML/HTML instead of an image. */
const probeWithFetch = async (url: string): Promise<"loaded" | "missing"> => {
	const response = await fetch(url, {
		method: "GET",
		mode: "cors",
		credentials: "omit",
		cache: "no-store",
	});
	if (response.ok && isImageResponse(response)) {
		await response.body?.cancel();
		return "loaded";
	}
	await response.body?.cancel();
	return "missing";
};

const probeWithImage = (
	url: string,
	onLoad: () => void,
	onMissing: () => void,
): HTMLImageElement => {
	const probe = new Image();
	probe.onload = onLoad;
	probe.onerror = onMissing;
	probe.src = url;
	return probe;
};

/**
 * Loads `url` off-screen, retrying while it 404s; calls `onLoad` once it has
 * loaded or `onGiveUp` after the last retry. Returns a cancel function.
 */
export function loadPendingImage(
	url: string,
	onLoad: () => void,
	onGiveUp: () => void,
): () => void {
	if (loadedUrls.has(url)) {
		onLoad();
		return () => {};
	}

	let attempt = 0;
	let timer: ReturnType<typeof setTimeout> | undefined;
	let probe: HTMLImageElement | undefined;
	let cancelled = false;

	const settleLoaded = () => {
		if (cancelled) {
			return;
		}
		loadedUrls.add(url);
		onLoad();
	};

	const scheduleRetry = () => {
		if (cancelled) {
			return;
		}
		const delay = RETRY_DELAYS_MS[attempt++];
		if (delay === undefined) {
			onGiveUp();
			return;
		}
		timer = setTimeout(() => {
			void runProbe();
		}, delay);
	};

	const runImageProbe = () => {
		if (cancelled) {
			return;
		}
		probe = probeWithImage(url, settleLoaded, scheduleRetry);
	};

	const runProbe = async () => {
		if (cancelled) {
			return;
		}
		try {
			const result = await probeWithFetch(url);
			if (cancelled) {
				return;
			}
			if (result === "loaded") {
				settleLoaded();
				return;
			}
			scheduleRetry();
		} catch {
			runImageProbe();
		}
	};

	void runProbe();

	return () => {
		cancelled = true;
		clearTimeout(timer);
		if (probe) {
			probe.onload = null;
			probe.onerror = null;
		}
	};
}

export type PendingImage = {
	/** The URL once it has loaded; undefined while pending or after giving up. */
	src: Accessor<string | undefined>;
	/** True once every retry has failed. */
	failed: Accessor<boolean>;
};

/** loadPendingImage for a reactive URL, exposing it once it has loaded. */
export function usePendingImage(
	url: Accessor<string | null | undefined>,
): PendingImage {
	const [src, setSrc] = createSignal<string>();
	const [failed, setFailed] = createSignal(false);

	createEffect(() => {
		const current = url();
		setSrc(undefined);
		setFailed(false);
		if (!current) {
			return;
		}
		onCleanup(
			loadPendingImage(
				current,
				() => setSrc(current),
				() => setFailed(true),
			),
		);
	});

	return { src, failed };
}
